import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../api/client';
import type { Tag } from '../types';

type Props = {
  value: string;
  onChange: (next: string) => void;
  onSubmit: (value: string) => void;
  /** Names to hide from suggestions — typically tags already assigned. */
  excludeNames?: readonly string[];
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Visual width of the input; suggestions match it. */
  width?: number;
};

const MAX_SUGGESTIONS = 8;

/** Free-text input with a dropdown of matching existing tag names.
 *  - Typing filters approved tags (case-insensitive substring) and
 *    excludes any already on excludeNames.
 *  - ↑/↓ navigates, Enter selects the highlighted suggestion (or
 *    submits the raw value if nothing is highlighted), Esc closes.
 *  - The user is free to type a brand-new tag name and submit — the
 *    backend will create it (admin path) on the server side.
 *
 *  Tag list is fetched once on mount; the set is small so caching
 *  in a global store wasn't worth the wiring. */
export default function TagAutocomplete({
  value, onChange, onSubmit, excludeNames = [], placeholder, maxLength = 30, autoFocus, disabled, width = 200,
}: Props) {
  const [allTags, setAllTags] = useState<Tag[] | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    api<{ data: Tag[] }>('/tags')
      .then(({ data }) => setAllTags(data))
      .catch(() => setAllTags([]));
  }, []);

  const suggestions = useMemo(() => {
    if (!allTags) return [];
    const q = value.trim().toLowerCase();
    const excluded = new Set(excludeNames.map((n) => n.toLowerCase()));
    return allTags
      .filter((t) => !excluded.has(t.name))
      .filter((t) => (q === '' ? true : t.name.toLowerCase().includes(q)))
      .slice(0, MAX_SUGGESTIONS);
  }, [allTags, value, excludeNames]);

  function select(name: string) {
    onChange(name);
    setOpen(false);
    setHighlight(-1);
    // Submit immediately on click/enter-of-suggestion — the user
    // explicitly picked an existing tag, no need for a second confirm.
    onSubmit(name);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlight >= 0 && highlight < suggestions.length) {
        select(suggestions[highlight].name);
      } else {
        // Submit whatever's in the input — may be a new tag name.
        const v = value.trim();
        if (v) onSubmit(v);
      }
    }
  }

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Defer close so click on a suggestion lands before the input
          // unmounts the dropdown.
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={onKey}
        maxLength={maxLength}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        autoComplete="off"
        style={{ padding: '2px 6px', fontSize: 12, width }}
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 2,
            padding: 0,
            listStyle: 'none',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-tooltip)',
            borderRadius: 4,
            boxShadow: 'var(--shadow-tooltip)',
            minWidth: width,
            zIndex: 10,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {suggestions.map((t, i) => (
            <li
              key={t.id}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                // mousedown not click — click fires after blur, which
                // would close the dropdown before the select() runs.
                e.preventDefault();
                if (blurTimer.current) window.clearTimeout(blurTimer.current);
                select(t.name);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
                background: i === highlight ? 'var(--pill-bg-tag)' : 'transparent',
                color: 'var(--fg)',
              }}
            >
              {t.name}
              {t.archive_count != null && (
                <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>×{t.archive_count}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
